// ── Версия сборки (видна в углу меню; бампать вместе с sw.js CACHE_VERSION) ──
export const BUILD = "v16";

// ── Игровые константы ───────────────────────────────────────────────────────
export const GAME = {
  WIDTH:  960,
  HEIGHT: 540,
  GROUND_Y: 460,         // уровень пола
  GRAVITY: 1500,
} as const;

export const FIGHTER = {
  WIDTH:  80,
  HEIGHT: 160,
  HP_MAX: 100,
  WALK_SPEED: 250,
  JUMP_SPEED: 700,

  // Базовые удары (для совместимости со старым кодом)
  PUNCH_DAMAGE: 8,
  KICK_DAMAGE:  12,
  PUNCH_RANGE: 90,
  KICK_RANGE:  110,
  PUNCH_RECOVERY: 250,
  KICK_RECOVERY:  400,

  HIT_STUN:       300,
  BLOCK_REDUCTION: 0.3,
} as const;

// Расширенные виды ударов (6-кнопочная схема LP/HP/LK/HK/BLK/RUN)
// Mapping (button + direction):
//   LP                  → jab          (быстрый прямой)
//   →LP                 → straight     (стойкий удар)
//   HP                  → hp           (тяжёлый прямой)
//   ↓HP                 → uppercut     (knockup)
//   LK                  → lk           (front kick)
//   HK                  → kick         (round kick)
//   ↓HK                 → sweep        (knockdown)
//   ↑→LK / ↑→HK         → jump_kick    (overhead)
//   LP+LK (рядом)       → throw        (command-throw)
export type AttackKind =
  | "jab" | "straight" | "hp" | "uppercut"
  | "lk" | "kick" | "sweep"
  | "jump_kick" | "throw";

export const ATTACKS: Record<AttackKind, {
  damage: number; range: number; recovery: number;
  hitstun: number; knockup?: boolean; knockdown?: boolean;
  vyOnHit?: number; vxOnHit?: number;
  shakeDur?: number; shakeIntensity?: number;
  freezeMs?: number; flash?: number;
  superGain?: number;                 // % super-meter за нанесение
}> = {
  jab: {
    damage: 6, range: 130, recovery: 220,
    hitstun: 220, vxOnHit: 140,
    shakeDur: 60, shakeIntensity: 0.003, freezeMs: 35, flash: 0xffffff,
    superGain: 4,
  },
  straight: {
    damage: 9, range: 145, recovery: 280,
    hitstun: 260, vxOnHit: 180,
    shakeDur: 80, shakeIntensity: 0.004, freezeMs: 50, flash: 0xffffaa,
    superGain: 5,
  },
  hp: {
    damage: 14, range: 140, recovery: 420,
    hitstun: 360, vxOnHit: 240,
    shakeDur: 120, shakeIntensity: 0.007, freezeMs: 80, flash: 0xff8800,
    superGain: 8,
  },
  uppercut: {
    damage: 18, range: 120, recovery: 600,
    hitstun: 500, knockup: true, vyOnHit: -550, vxOnHit: 60,
    shakeDur: 200, shakeIntensity: 0.012, freezeMs: 110, flash: 0xffd700,
    superGain: 10,
  },
  lk: {
    damage: 7, range: 135, recovery: 260,
    hitstun: 220, vxOnHit: 160,
    shakeDur: 70, shakeIntensity: 0.003, freezeMs: 40, flash: 0xffffff,
    superGain: 4,
  },
  kick: {
    damage: 12, range: 150, recovery: 380,
    hitstun: 320, vxOnHit: 220,
    shakeDur: 90, shakeIntensity: 0.005, freezeMs: 60, flash: 0xff8800,
    superGain: 7,
  },
  sweep: {
    damage: 10, range: 140, recovery: 520,
    hitstun: 600, knockdown: true, vxOnHit: 280,
    shakeDur: 130, shakeIntensity: 0.008, freezeMs: 80, flash: 0xff3030,
    superGain: 8,
  },
  jump_kick: {
    damage: 11, range: 145, recovery: 340,
    hitstun: 340, vxOnHit: 200,
    shakeDur: 100, shakeIntensity: 0.006, freezeMs: 60, flash: 0xffaa00,
    superGain: 7,
  },
  throw: {
    damage: 16, range: 60, recovery: 480,
    hitstun: 700, knockdown: true, vxOnHit: 260, vyOnHit: -300,
    shakeDur: 140, shakeIntensity: 0.008, freezeMs: 100, flash: 0xff4488,
    superGain: 9,
  },
};

export const SUPER_METER = {
  MAX: 100,
  GAIN_BLOCKED: 1,
  GAIN_TAKEN: 3,
} as const;

// 4 архетипа из docs/MOVE_LIST.md — параметры различаются по HP/скорости/прыжку.
export type Archetype = "leo" | "mike" | "don" | "raph" | "default";

export const ARCHETYPES: Record<Archetype, {
  hp: number; walk: number; jump: number; label: string;
}> = {
  leo:     { hp: 100, walk: 250, jump: 700, label: "ЛЕО — БАЛАНС" },
  mike:    { hp:  85, walk: 320, jump: 750, label: "МАЙК — СКОРОСТЬ" },
  don:     { hp:  95, walk: 220, jump: 680, label: "ДОН — ТЕХНИКА" },
  raph:    { hp: 115, walk: 200, jump: 650, label: "РАФ — МОЩЬ" },
  default: { hp: 100, walk: 250, jump: 700, label: "БОЕЦ" },
};

export const ROUND = {
  TIMER_SECONDS: 60,
  ROUNDS_TO_WIN: 2,      // best of 3
} as const;

// ── Управление ────────────────────────────────────────────────────────────────
// 6-кнопочная схема: LP/HP/LK/HK/BLK/RUN. Поля PUNCH/KICK сохранены для backward-compat.
export const P1_KEYS = {
  LEFT:  "A", RIGHT: "D", UP: "W", DOWN: "S",
  LP: "F", HP: "G", LK: "V", HK: "B",
  PUNCH: "F", KICK: "V",   // legacy alias
  BLOCK: "H", RUN: "T",
} as const;

export const P2_KEYS = {
  LEFT: "LEFT", RIGHT: "RIGHT", UP: "UP", DOWN: "DOWN",
  LP: "K", HP: "L", LK: "COMMA", HK: "PERIOD",
  PUNCH: "K", KICK: "COMMA",
  BLOCK: "SEMICOLON", RUN: "SLASH",
} as const;

// ── Цвета (placeholder, до спрайтов) ─────────────────────────────────────────
export const COLORS = {
  BG_TOP:    0x1a0505,
  BG_BOTTOM: 0x000000,
  GROUND:    0x2a1810,
  P1_COLOR:  0xc41e3a,   // красный (Banzai brand)
  P2_COLOR:  0xfafafa,   // белый
  HP_FULL:   0xffd700,
  HP_BG:     0x440000,
  TEXT:      0xffffff,
  ACCENT:    0xff3030,
} as const;
